<template>
    <v-card class="add-duck-form pa-4">
        <v-card-title class="text-h6 mb-4 sticky-header">Add Duck(s)</v-card-title>
        
        <!-- Warning Message -->
        <v-alert
          v-if="props.warningMessage"
          type="warning"
          variant="tonal"
          border="start"
          class="mb-4"
          icon="mdi-alert-circle-outline"
          closable
        >
          {{ props.warningMessage }}
        </v-alert>
        
        <!-- Main scrollable content -->
        <div class="form-scroll-container">
          <v-form @submit.prevent="handleSubmit" class="form-content">
              <!-- Duck Creation Mode -->
              <v-btn-toggle
                  v-model="creationMode"
                  color="primary"
                  mandatory
                  variant="outlined"
                  class="mb-4 w-full toggle-container"
              >
                  <v-btn value="single" class="w-1/2 toggle-btn">
                      <v-icon start>mdi-duck</v-icon>
                      Single Duck
                  </v-btn>
                  <v-btn value="multiple" class="w-1/2 toggle-btn">
                      <div class="flex items-center justify-center">
                          <v-icon>mdi-layers-plus</v-icon>
                          <span class="ml-2">Multiple Ducks</span>
                      </div>
                  </v-btn>
              </v-btn-toggle>

              <!-- Image Selection (Visual Toggle) -->
              <div class="mb-4">
                  <p class="text-body-1 font-medium mb-2">Duck Image:</p>
                  <div class="image-selector-container">
                      <div 
                          class="image-option" 
                          :class="{'selected': useDefaultImage}"
                          @click="useDefaultImage = true"
                      >
                          <div class="image-preview">
                              <img src="/ducksImages/duckIcon.webp" alt="Default Duck" class="preview-image" />
                          </div>
                          <div class="image-label">
                              <v-icon v-if="useDefaultImage" color="primary" class="mr-1">mdi-check-circle</v-icon>
                              Default Duck
                          </div>
                      </div>
                      
                      <div 
                          class="image-option" 
                          :class="{'selected': !useDefaultImage}"
                          @click="useDefaultImage = false"
                      >
                          <div class="image-preview">
                              <div v-if="imageFile && imagePreview" class="custom-preview">
                                  <img :src="imagePreview" alt="Custom Duck" class="preview-image" />
                              </div>
                              <div v-else class="upload-placeholder">
                                  <v-icon size="40" color="grey-lighten-1">mdi-file-image-plus</v-icon>
                              </div>
                          </div>
                          <div class="image-label">
                              <v-icon v-if="!useDefaultImage" color="primary" class="mr-1">mdi-check-circle</v-icon>
                              Custom Image
                          </div>
                      </div>
                  </div>
                  
                  <!-- File Input (only shown when custom image is selected) -->
                  <v-slide-y-transition>
                      <v-file-input
                          v-if="!useDefaultImage"
                          v-model="imageFile"
                          accept="image/*"
                          label="Select Image"
                          :required="!useDefaultImage"
                          class="mt-3"
                          @update:model-value="handleImageChange"
                          hint="Select an image file (JPG, PNG, or WebP)"
                          persistent-hint
                          variant="outlined"
                          density="comfortable"
                      />
                  </v-slide-y-transition>
              </div>

              <!-- Quantity Selector (moved below image selection) -->
              <div v-if="creationMode === 'multiple'" class="mb-4">
                  <p class="text-body-1 font-medium mb-2">Number of Ducks:</p>
                  <v-text-field
                      v-model.number="quantity"
                      type="number"
                      label="Quantity"
                      min="1"
                      :rules="[
                          v => !!v || 'Quantity is required',
                          v => v >= 1 || 'Quantity must be at least 1',
                          v => Number.isInteger(Number(v)) || 'Quantity must be a whole number',
                          v => v <= remainingDucksAllowed || `Maximum of ${remainingDucksAllowed} ducks can be added`
                      ]"
                      variant="outlined"
                      inputmode="numeric"
                      pattern="[0-9]*"
                  ></v-text-field>
              </div>

              <!-- Duck Limit Information (moved to the very bottom of the page) -->
              <v-divider class="my-4"></v-divider>
              <div v-if="props.cruiseInfo" class="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mt-4 mb-6">
                  <p class="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Duck Quota</p>
                  <div class="flex justify-between mb-2">
                      <span>Created Ducks:</span>
                      <span>{{ props.cruiseInfo.ducksCreated || 0 }}</span>
                  </div>
                  <div class="flex justify-between mb-2">
                      <span>Maximum Allowed:</span>
                      <span>{{ props.cruiseInfo.maxDucksAllowed || 0 }}</span>
                  </div>
                  <div class="flex justify-between font-medium">
                      <span>Remaining:</span>
                      <span>{{ remainingDucksAllowed }}</span>
                  </div>
                  
                  <!-- Add a progress bar for visual representation -->
                  <div class="mt-3 relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                          class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all"
                          :style="`width: ${calculatePercentage()}%`"
                      ></div>
                  </div>
              </div>
          </v-form>
        </div>

        <!-- Fixed action buttons at bottom -->
        <div class="action-buttons">
            <v-btn variant="outlined" @click="$emit('close')">Cancel</v-btn>
            <v-btn
                type="submit"
                color="primary"
                :loading="loading"
                :disabled="isSubmitDisabled"
                @click="handleSubmit"
            >
                {{ submitButtonText }}
            </v-btn>
        </div>

        <v-snackbar
            v-model="showNotification"
            :color="notificationColor"
            :timeout="3000"
            location="top"
        >
            {{ notificationText }}
        </v-snackbar>
    </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useDemoCruiseStore } from '@/stores/demoCruiseStore';

interface DuckFormData {
    cruiseId: number;
    image: string;
}

const props = defineProps({
    cruiseId: {
        type: Number,
        required: true
    },
    cruiseInfo: {
        type: Object,
        default: null
    },
    warningMessage: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['close', 'created']);

const cruiseStore = useDemoCruiseStore();

// Form state
const loading = ref(false);
const imageFile = ref<File | null>(null);
const imagePreview = ref<string | null>(null);
const showNotification = ref(false);
const notificationText = ref('');
const notificationColor = ref('');
const creationMode = ref('single'); // 'single' or 'multiple'
const quantity = ref(1);
const useDefaultImage = ref(true); // Default to using default image

// Default image URL
const DEFAULT_DUCK_IMAGE = '/ducksImages/duckIcon.webp';

// Form data
const formData = ref<DuckFormData>({
    cruiseId: props.cruiseId,
    image: ''
});

// Calculate remaining ducks allowed
const remainingDucksAllowed = computed(() => {
    if (!props.cruiseInfo) return Infinity;
    const created = props.cruiseInfo.ducksCreated || 0;
    const max = props.cruiseInfo.maxDucksAllowed || 0;
    return Math.max(0, max - created);
});

// Computed properties
const isSubmitDisabled = computed(() => {
    if (useDefaultImage.value) {
        if (creationMode.value === 'multiple' && (!quantity.value || quantity.value < 1)) return true;
        if (remainingDucksAllowed.value === 0) return true;
        if (creationMode.value === 'multiple' && quantity.value > remainingDucksAllowed.value) return true;
        return false;
    }
    if (!imageFile.value) return true;
    if (creationMode.value === 'multiple' && (!quantity.value || quantity.value < 1)) return true;
    if (creationMode.value === 'multiple' && quantity.value > remainingDucksAllowed.value) return true;
    if (remainingDucksAllowed.value === 0) return true;
    return false;
});

const submitButtonText = computed(() => {
    if (creationMode.value === 'single') {
        return loading.value ? 'Adding Duck...' : 'Add Duck';
    } else {
        return loading.value ? `Adding ${quantity.value} Ducks...` : `Add ${quantity.value} Ducks`;
    }
});

// Load default image when useDefaultImage is toggled on
const setDefaultImage = async () => {
    if (useDefaultImage.value) {
        try {
            const response = await fetch(DEFAULT_DUCK_IMAGE);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onload = () => {
                formData.value.image = reader.result as string;
            };
            reader.readAsDataURL(blob);
        } catch (error) {
            console.error('Error loading default image:', error);
            showError('Failed to load default image');
        }
    }
};

// Watch for changes to useDefaultImage
watch(useDefaultImage, async (newValue) => {
    if (newValue) {
        await setDefaultImage();
    } else {
        // Clear image when switching back to custom image
        formData.value.image = '';
        // Don't clear imageFile so user can still see their selection
    }
});

// Watch for cruiseId changes
watch(() => props.cruiseId, (newValue) => {
    formData.value.cruiseId = newValue;
});

const handleImageChange = async (fileInput: File | File[]) => {
    if (!fileInput || (Array.isArray(fileInput) && fileInput.length === 0)) {
        imagePreview.value = null;
        return;
    }

    const file = Array.isArray(fileInput) ? fileInput[0] : fileInput;

    try {
        if (!file.type.startsWith('image/')) {
            showError('Please select an image file');
            imagePreview.value = null;
            return;
        }

        // Create image preview
        const reader = new FileReader();
        reader.onload = (e) => {
            imagePreview.value = e.target?.result as string;
            formData.value.image = e.target?.result as string;
        };
        reader.readAsDataURL(file);
    } catch (error) {
        console.error('Error processing image:', error);
        showError('Error processing image. Please try another image.');
    }
};

const showError = (message: string) => {
    notificationText.value = message;
    notificationColor.value = 'error';
    showNotification.value = true;
};

// Calculate percentage for progress bar
const calculatePercentage = () => {
    if (!props.cruiseInfo) return 0;
    const created = props.cruiseInfo.ducksCreated || 0;
    const max = props.cruiseInfo.maxDucksAllowed || 0;
    if (max === 0) return 0;
    return (created / max) * 100;
};

// Handle form submission
const handleSubmit = async () => {
    try {
        loading.value = true;
        
        // Set formData's cruiseId to ensure it's current
        formData.value.cruiseId = props.cruiseId;
        
        // Validate image
        if (!useDefaultImage.value && !formData.value.image) {
            showError('Please select an image');
            return;
        }
        
        // Use default image if selected
        if (useDefaultImage.value && !formData.value.image) {
            await setDefaultImage();
        }
        
        // For demo purposes, simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (creationMode.value === 'single') {
            // Add a single duck to the cruise
            const imageUrl = formData.value.image || DEFAULT_DUCK_IMAGE;
            cruiseStore.addDuckToCruise(props.cruiseId, { image: imageUrl });
        } else {
            // Add multiple ducks to the cruise
            const imageUrl = formData.value.image || DEFAULT_DUCK_IMAGE;
            for (let i = 0; i < quantity.value; i++) {
                cruiseStore.addDuckToCruise(props.cruiseId, { image: imageUrl });
                // Simulate each duck taking some time to create
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }
        
        // Show success message
        notificationText.value = creationMode.value === 'single' 
            ? 'Duck added successfully!' 
            : `${quantity.value} ducks added successfully!`;
        notificationColor.value = 'success';
        showNotification.value = true;
        
        // Notify parent component
        emit('created');
        emit('close');
    } catch (error) {
        console.error('Error adding duck(s):', error);
        showError('Failed to add duck(s). Please try again.');
    } finally {
        loading.value = false;
    }
};

// Initialize
onMounted(async () => {
    // Load default image on mount if using default image
    if (useDefaultImage.value) {
        await setDefaultImage();
    }
});
</script>

<style scoped>
.add-duck-form {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 90vh;
    overflow: hidden;
}

.sticky-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
    padding-bottom: 8px;
}

.form-scroll-container {
    flex: 1;
    overflow-y: auto;
    padding-right: 8px;
    margin-right: -8px;
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.form-scroll-container::-webkit-scrollbar {
    width: 4px;
}

.form-scroll-container::-webkit-scrollbar-track {
    background: transparent;
}

.form-scroll-container::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.image-selector-container {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 8px;
}

.image-option {
    flex: 1;
    border: 2px solid transparent;
    border-radius: 8px;
    padding: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.image-option:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.image-option.selected {
    border-color: var(--v-theme-primary);
    background-color: rgba(var(--v-theme-primary-rgb), 0.05);
}

.image-preview {
    width: 100px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    border-radius: 4px;
    overflow: hidden;
    background-color: #f5f5f5;
}

.preview-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.image-label {
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.upload-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #eee;
}

.custom-preview {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.toggle-container {
    width: 100%;
    border-radius: 4px;
    overflow: hidden;
}

.toggle-btn {
    border-radius: 0 !important;
    height: 48px;
}

@media (max-width: 600px) {
    .image-selector-container {
        flex-direction: column;
    }
    
    .image-option {
        flex-direction: row;
        padding: 12px;
    }
    
    .image-preview {
        width: 60px;
        height: 60px;
        margin-bottom: 0;
        margin-right: 12px;
    }
    
    .image-label {
        flex: 1;
        justify-content: flex-start;
    }
}
</style>
